import axios from 'axios';
import { apiClient } from '@/shared/lib/axios';
import type {
  PresignedUrlResponse,
  PresignPayload,
  PresignResponse,
  UploadPortal,
  UploadPortalParams,
  UploadTokenMetadata,
} from '@/types/upload';

/** Build the portal-specific base path for upload endpoints. */
const buildBasePath = (portal: UploadPortal, params: UploadPortalParams): string => {
  switch (portal) {
    case 'admin':
      return `/admin/${params.siteType}/uploads`;
    case 'clinic':
      if (!params.clinicNumber) {
        throw new Error('uploadService: clinicNumber is required for clinic portal');
      }
      return `/clinic/${params.siteType}/${params.clinicNumber}/uploads`;
    case 'massager':
      return `/massager/${params.siteType}/uploads`;
  }
};

const unwrap = <T>(data: unknown): T => {
  if (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>)) {
    return (data as { data: T }).data;
  }
  return data as T;
};

/**
 * Request a presigned PUT URL from the backend.
 */
export const presignUpload = async (
  portal: UploadPortal,
  params: UploadPortalParams,
  payload: PresignPayload,
): Promise<PresignResponse> => {
  const res = await apiClient.post(`${buildBasePath(portal, params)}/presign`, payload);
  return unwrap<PresignResponse>(res.data);
};

/**
 * Upload the raw file directly to S3/MinIO using the presigned URL.
 * This call bypasses the Laravel backend — the browser talks to S3 directly.
 *
 * @param onProgress optional progress callback (0..1)
 * @param signal     optional AbortSignal for cancellation
 */
export const putFileToPresignedUrl = async (
  uploadUrl: string,
  file: File,
  requiredHeaders: Record<string, string>,
  onProgress?: (ratio: number) => void,
  signal?: AbortSignal,
): Promise<void> => {
  // Use a bare axios instance so our auth interceptors / base URL don't leak into the S3 PUT request.
  await axios.put(uploadUrl, file, {
    headers: requiredHeaders,
    signal,
    onUploadProgress: (e) => {
      if (onProgress && e.total) {
        onProgress(e.loaded / e.total);
      }
    },
    // Disable default transforms to avoid axios reserialising a Blob/File.
    transformRequest: [(data) => data],
  });
};

/**
 * Tell the backend that the upload finished — backend verifies object existence
 * on S3 and marks the token as "confirmed".
 */
export const confirmUpload = async (
  portal: UploadPortal,
  params: UploadPortalParams,
  token: string,
): Promise<UploadTokenMetadata> => {
  const res = await apiClient.post(`${buildBasePath(portal, params)}/confirm`, { token });
  return unwrap<UploadTokenMetadata>(res.data);
};

/**
 * Fetch token metadata (filename, size, status, ...).
 */
export const getUploadMetadata = async (
  portal: UploadPortal,
  params: UploadPortalParams,
  token: string,
): Promise<UploadTokenMetadata> => {
  const res = await apiClient.get(`${buildBasePath(portal, params)}/${token}`);
  return unwrap<UploadTokenMetadata>(res.data);
};

/**
 * Get a short-lived URL for inline viewing (e.g. <img>, <iframe>).
 */
export const getUploadViewUrl = async (
  portal: UploadPortal,
  params: UploadPortalParams,
  token: string,
): Promise<PresignedUrlResponse> => {
  const res = await apiClient.get(`${buildBasePath(portal, params)}/${token}/view`);
  return unwrap<PresignedUrlResponse>(res.data);
};

/**
 * Get a short-lived URL that forces browser download.
 */
export const getUploadDownloadUrl = async (
  portal: UploadPortal,
  params: UploadPortalParams,
  token: string,
): Promise<PresignedUrlResponse> => {
  const res = await apiClient.get(`${buildBasePath(portal, params)}/${token}/download`);
  return unwrap<PresignedUrlResponse>(res.data);
};

/**
 * Delete the token (and its underlying file). Only works on tokens that are not
 * yet attached to an entity.
 */
export const deleteUpload = async (
  portal: UploadPortal,
  params: UploadPortalParams,
  token: string,
): Promise<void> => {
  await apiClient.delete(`${buildBasePath(portal, params)}/${token}`);
};
